import { useEffect } from 'react'
import { useWorkspaceBranding } from 'src/utils/workspace-logo'
import {
  DEFAULT_COVER_SELECTOR,
  defaultCoverCss,
  defaultThumbBackground,
} from 'src/utils/default-header-background'
import {
  DEFAULT_COVER_VARIANTS,
  coverVariantFor,
} from 'src/utils/default-cover-art'

const STYLE_ID = 'mcg-default-cover'
const MARK = 'data-mcg-cover'
/** Set on a card (or its cover) by the course or path it shows. */
const KEY_SELECTOR = '[data-cover-key], [data-catalog-key]'

/** Width / height of an element's box (cards are about 1.6). */
const aspectOf = el => {
  const { width, height } = el.getBoundingClientRect()
  return width > 0 && height > 0 ? width / height : 1.6
}

/**
 * Gives every default cover on the page its own art: the n-th one in page
 * order gets variant n (0–4), then the set starts over, so a list of
 * courses or paths without covers reads as a set rather than one repeated
 * image. Covers keep their place in the order once given one.
 */
/** Whether a painted element got a real cover since (an image was added). */
const hasOwnCover = el => {
  if (el.matches(DEFAULT_COVER_SELECTOR)) return false
  if (el.tagName === 'IMG') return true
  return !String(el.style.backgroundImage || '').includes('data:image/svg')
}

const unpaint = el => {
  el.removeAttribute(MARK)
  el.style.removeProperty('object-position')
  if (el.tagName === 'IMG') el.style.removeProperty('background')
}

const paintCovers = color => {
  const covers = Array.from(
    document.querySelectorAll(`${DEFAULT_COVER_SELECTOR}, [${MARK}]`),
  ).filter(el => {
    if (el.hasAttribute(MARK) && hasOwnCover(el)) {
      unpaint(el)
      return false
    }
    return true
  })
  covers.forEach((el, index) => {
    // A course or path keeps its own art (from its key); covers with no
    // key nearby take the next one in page order.
    const keyed = el.closest(KEY_SELECTOR)
    const coverKey =
      keyed &&
      (keyed.getAttribute('data-cover-key') ||
        keyed.getAttribute('data-catalog-key'))
    const variant = coverKey
      ? coverVariantFor(coverKey)
      : index % DEFAULT_COVER_VARIANTS
    const aspect = Math.round(aspectOf(el) * 10) / 10
    // Painted already with this art, color and shape: leave it (setting the
    // same style again would wake the observer for nothing).
    const key = `${variant}|${color}|${aspect}`
    if (el.getAttribute(MARK) === key) return
    el.setAttribute(MARK, key)
    const background = defaultThumbBackground(null, variant, aspect)
    if (el.tagName === 'IMG') {
      // Keep the <img> (and its size); show the art behind an empty image.
      el.style.setProperty('object-position', '-99999px -99999px', 'important')
    }
    el.style.setProperty('background', background, 'important')
  })
}

/**
 * Draws every default cover (the Default header image and the "no cover"
 * card images) in the workspace's primary color, wherever it appears:
 * course and path cards, lists and landing page blocks.
 */
const DefaultCoverStyle = () => {
  const branding = useWorkspaceBranding()

  useEffect(() => {
    let style = document.getElementById(STYLE_ID)
    if (!style) {
      style = document.createElement('style')
      style.id = STYLE_ID
      document.head.appendChild(style)
    }
    // Until the script below runs: the first art for all of them.
    style.textContent = defaultCoverCss(defaultThumbBackground(null, 0))

    let frame = null
    const schedule = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = null
        paintCovers(branding?.primaryColor || '')
      })
    }
    schedule()
    const observer = new MutationObserver(schedule)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'style'],
    })
    return () => {
      observer.disconnect()
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [branding])

  return null
}

export default DefaultCoverStyle
